import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Plus, Route, Save, Search, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Button } from '@/Components/ui/Button';
import { Card } from '@/Components/ui/Card';
import { IconButton } from '@/Components/ui/IconButton';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { PageHeader } from '@/Components/data/PageHeader';
import DecimalInput from '@/Components/forms/DecimalInput';
import { Field } from '@/Components/forms/Field';
import FormStickyFooter from '@/Components/forms/FormStickyFooter';
import roteiroService, { type RoteiroInput, type RoteiroOperacao } from '@/services/roteiro';
import produtoService, { type Produto } from '@/services/produto';
import centroTrabalhoService, { type CentroTrabalho } from '@/services/centroTrabalho';
import { getUuidCandidate, isUuid } from '@/utils/uuid';

const FORM_VAZIO: RoteiroInput = { produto_id: '', versao: '1.0', tempo_total_padrao: 0, status: 'RASCUNHO', operacoes: [] };

type ApiValidationResponse = {
  message?: string;
  errors?: Record<string, string[]>;
};

const produtoLabel = (produto: Produto) => `${produto.codigo || '-'} - ${produto.descricao || ''}`;

function extractErrorMessages(error: unknown): string[] {
  if (error instanceof AxiosError) {
    const data = error.response?.data as ApiValidationResponse | Record<string, unknown> | undefined;
    if (data && 'errors' in data && data.errors && typeof data.errors === 'object') {
      return Object.entries(data.errors as Record<string, string[]>).flatMap(([field, messages]) =>
        messages.map((message) => `${field}: ${message}`),
      );
    }
    if (data && 'message' in data && typeof data.message === 'string') return [data.message];
    if (data && typeof data === 'object') {
      const topLevelErrors = Object.entries(data).flatMap(([field, value]) => {
        if (!Array.isArray(value)) return [];
        return value.map((message) => `${field}: ${message}`);
      });
      if (topLevelErrors.length > 0) return topLevelErrors;
    }
  }

  return ['Não foi possível salvar o roteiro. Verifique os dados e tente novamente.'];
}

type ProductAutocompleteFieldProps = {
  id?: string;
  produtos: Produto[];
  value?: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
};

function ProductAutocompleteField({
  id,
  produtos,
  value,
  onChange,
  disabled = false,
  required = false,
  placeholder = 'Buscar produto...',
}: ProductAutocompleteFieldProps) {
  const [open, setOpen] = useState(false);
  const selectedProduto = produtos.find((produto) => getUuidCandidate(produto) === value);
  const [text, setText] = useState('');
  const termo = text.trim().toLowerCase();

  useEffect(() => {
    setText(selectedProduto ? produtoLabel(selectedProduto) : '');
  }, [selectedProduto]);

  const sugestoes = (termo
    ? produtos.filter((produto) => produtoLabel(produto).toLowerCase().includes(termo))
    : produtos
  ).slice(0, 12);

  return (
    <div className="nx-autocomplete-filter">
      <div className="nx-search-field nx-search-field--filter nx-autocomplete-filter__control">
        <Search size={15} strokeWidth={2} aria-hidden />
        <input
          id={id}
          type="search"
          value={text}
          disabled={disabled}
          required={required}
          placeholder={placeholder}
          aria-label={placeholder}
          onChange={(event) => {
            setText(event.target.value);
            if (value) onChange('');
            setOpen(true);
          }}
          onFocus={() => !disabled && setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 140)}
        />
        {text && !disabled ? (
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setText('');
              onChange('');
            }}
            aria-label="Limpar produto"
          >
            ×
          </button>
        ) : null}
      </div>
      {open && !disabled ? (
        <div className="nx-autocomplete-filter__menu">
          {sugestoes.length === 0 ? (
            <div className="nx-autocomplete-filter__empty">Nenhum produto encontrado</div>
          ) : (
            sugestoes.map((produto) => {
              const uuid = getUuidCandidate(produto) || '';
              return (
                <button
                  type="button"
                  key={produto.id}
                  className={uuid === value ? 'is-selected' : undefined}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onChange(uuid);
                    setText(produtoLabel(produto));
                    setOpen(false);
                  }}
                >
                  <strong>{produto.codigo || '-'}</strong>
                  <span>{produto.descricao}</span>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function RoteiroForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editando = Boolean(id);
  const [form, setForm] = useState<RoteiroInput>(FORM_VAZIO);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [centros, setCentros] = useState<CentroTrabalho[]>([]);
  const [loading, setLoading] = useState(editando);
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState<string[]>([]);
  const produtosValidos = produtos.filter((produto) => getUuidCandidate(produto));
  const tempoTotalCalculado = useMemo(
    () => (form.operacoes ?? []).reduce((total, op) => total + (Number(op.tempo_padrao_minutos) || 0) + (Number(op.tempo_setup_minutos) || 0), 0),
    [form.operacoes],
  );

  useEffect(() => {
    produtoService.listar().then(setProdutos).catch(console.error);
    centroTrabalhoService.listar().then(setCentros).catch(console.error);
  }, []);
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    roteiroService.buscarPorId(id)
      .then((data) => setForm({ produto_id: data.produto_id, versao: data.versao, tempo_total_padrao: data.tempo_total_padrao ?? 0, status: data.status, operacoes: data.operacoes ?? [] }))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const adicionarOperacao = () => {
    setForm({
      ...form,
      operacoes: [
        ...(form.operacoes ?? []),
        {
          sequencia: ((form.operacoes?.length ?? 0) + 1) * 10,
          operacao: '',
          centro_trabalho_id: null,
          tempo_padrao_minutos: 0,
          tempo_setup_minutos: 0,
        },
      ],
    });
  };

  const atualizarOperacao = (index: number, patch: Partial<RoteiroOperacao>) => {
    const operacoes = [...(form.operacoes ?? [])];
    operacoes[index] = { ...operacoes[index], ...patch };
    setForm({ ...form, operacoes });
  };

  const removerOperacao = (index: number) => {
    setForm({ ...form, operacoes: (form.operacoes ?? []).filter((_, itemIndex) => itemIndex !== index) });
  };

  const salvar = async () => {
    const errosFormulario: string[] = [];
    if (!isUuid(form.produto_id)) errosFormulario.push('Produto: selecione um produto válido na lista.');
    if (!form.versao) errosFormulario.push('Versão: informe a versão do roteiro.');
    (form.operacoes ?? []).forEach((operacao, index) => {
      if (!operacao.sequencia) errosFormulario.push(`Operação ${index + 1}: informe a sequência.`);
      if (!operacao.operacao?.trim()) errosFormulario.push(`Operação ${index + 1}: informe a descrição da operação.`);
      if (operacao.centro_trabalho_id && !isUuid(operacao.centro_trabalho_id)) errosFormulario.push(`Operação ${index + 1}: selecione um centro de trabalho válido.`);
      if (!operacao.tempo_padrao_minutos || Number(operacao.tempo_padrao_minutos) <= 0) errosFormulario.push(`Operação ${index + 1}: informe o tempo padrão maior que zero.`);
    });

    if (errosFormulario.length > 0) {
      setErros(errosFormulario);
      return;
    }

    setErros([]);
    setSalvando(true);
    try {
      const payload: RoteiroInput = {
        produto_id: form.produto_id,
        versao: form.versao,
        tempo_total_padrao: tempoTotalCalculado,
        status: form.status,
        operacoes: (form.operacoes ?? []).map((operacao) => ({
          sequencia: Number(operacao.sequencia),
          operacao: operacao.operacao,
          centro_trabalho_id: operacao.centro_trabalho_id || null,
          tempo_padrao_minutos: Number(operacao.tempo_padrao_minutos) || 0,
          tempo_setup_minutos: Number(operacao.tempo_setup_minutos) || 0,
        })),
      };

      if (id) await roteiroService.atualizar(id, payload);
      else await roteiroService.criar(payload);
      navigate('/producao/roteiros');
    } catch (error) {
      console.error('Erro ao salvar roteiro', error);
      setErros(extractErrorMessages(error));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <form className="nx-page nx-sngpc-form-page" noValidate onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
      <PageHeader icon={<Route size={21} />} title={editando ? 'Editar Roteiro' : 'Novo Roteiro'} description="Configure o produto, versão e tempos padrão do roteiro." actions={<Button type="button" variant="ghost" onClick={() => navigate('/producao/roteiros')}><ArrowLeft size={16} /> Voltar</Button>} />
      <Card className="nx-page-card nx-sngpc-form-card">
        {erros.length > 0 ? (
          <div className="nx-form-alert nx-form-alert--error" role="alert">
            <strong>Não foi possível salvar o roteiro.</strong>
            <ul>
              {erros.map((erro, index) => (
                <li key={`${erro}-${index}`}>{erro}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <section className="nx-sngpc-form-section">
          <div className="nx-sngpc-form-section__header"><h2>Dados do roteiro</h2></div>
          <div className="nx-sngpc-page-form-grid">
            <Field label="Produto *" htmlFor="roteiro-produto" className="nx-sngpc-field--full">
              <ProductAutocompleteField
                id="roteiro-produto"
                produtos={produtosValidos}
                value={form.produto_id}
                onChange={(value) => setForm({ ...form, produto_id: value })}
                disabled={loading || editando}
                required
                placeholder="Buscar produto..."
              />
            </Field>
            <Field label="Versão *" htmlFor="roteiro-versao"><Input id="roteiro-versao" value={form.versao} onChange={(e) => setForm({ ...form, versao: e.target.value })} disabled={loading} required /></Field>
            <Field label="Tempo total padrão (min)" htmlFor="roteiro-tempo"><Input id="roteiro-tempo" value={tempoTotalCalculado} readOnly disabled={loading} /></Field>
            <Field label="Status" htmlFor="roteiro-status">
              <Select id="roteiro-status" value={form.status || 'RASCUNHO'} onChange={(e) => setForm({ ...form, status: e.target.value })} disabled={loading}>
                <option value="RASCUNHO">Rascunho</option><option value="APROVADO">Aprovado</option><option value="OBSOLETO">Obsoleto</option>
              </Select>
            </Field>
          </div>
        </section>

        <section className="nx-sngpc-form-section">
          <div className="nx-sngpc-form-section__header">
            <h2>Operações</h2>
            <Button type="button" variant="ghost" onClick={adicionarOperacao} disabled={loading}>
              <Plus size={15} />
              Adicionar
            </Button>
          </div>

          <div className="nx-table-wrap nx-sngpc-field--full">
            <table className="nx-table nx-sngpc-table">
              <thead>
                <tr>
                  <th>Seq</th>
                  <th>Operação *</th>
                  <th>Centro de Trabalho</th>
                  <th>Tempo (min) *</th>
                  <th>Setup (min)</th>
                  <th className="nx-table-actions">Ações</th>
                </tr>
              </thead>
              <tbody>
                {(form.operacoes ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="nx-sngpc-empty-cell">Nenhuma operação adicionada.</td>
                  </tr>
                ) : (
                  (form.operacoes ?? []).map((operacao, index) => (
                    <tr key={index}>
                      <td>
                        <Input
                          type="number"
                          value={operacao.sequencia ?? ''}
                          onChange={(event) => atualizarOperacao(index, { sequencia: Number(event.target.value) })}
                          disabled={loading}
                        />
                      </td>
                      <td>
                        <Input
                          value={operacao.operacao}
                          onChange={(event) => atualizarOperacao(index, { operacao: event.target.value })}
                          placeholder="Ex: Pesagem"
                          disabled={loading}
                        />
                      </td>
                      <td>
                        <Select
                          value={operacao.centro_trabalho_id || ''}
                          onChange={(event) => atualizarOperacao(index, { centro_trabalho_id: event.target.value || null })}
                          disabled={loading}
                        >
                          <option value="">-</option>
                          {centros.map((centro) => (
                            <option key={centro.id} value={getUuidCandidate(centro) || ''}>{centro.codigo} - {centro.descricao}</option>
                          ))}
                        </Select>
                      </td>
                      <td>
                        <DecimalInput
                          value={operacao.tempo_padrao_minutos ?? ''}
                          onValueChange={(value) => atualizarOperacao(index, { tempo_padrao_minutos: value ?? 0 })}
                          disabled={loading}
                        />
                      </td>
                      <td>
                        <DecimalInput
                          value={operacao.tempo_setup_minutos ?? ''}
                          onValueChange={(value) => atualizarOperacao(index, { tempo_setup_minutos: value ?? 0 })}
                          disabled={loading}
                        />
                      </td>
                      <td className="nx-table-actions">
                        <IconButton label="Remover" variant="danger" onClick={() => removerOperacao(index)} disabled={loading}>
                          <Trash2 size={15} strokeWidth={1.9} aria-hidden />
                        </IconButton>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </Card>
      <FormStickyFooter cancelLabel="Cancelar" onCancel={() => navigate('/producao/roteiros')} saveLabel="Salvar" loading={salvando} saveIcon={<Save size={16} />} />
    </form>
  );
}
